import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';

import { api } from '../lib/api.ts';
import { useAuth } from '../contexto/auth.tsx';
import { Aviso, Botao, Campo, Cartao } from '../componentes/ui.tsx';

const PERFIS: Record<string, string> = {
  admin: 'Administrador — acesso a tudo, inclusive usuários',
  regras: 'Regras — altera faixas, pisos e vigências',
  fechamento: 'Fechamento — calcula e fecha as competências',
  consulta: 'Consulta — somente leitura',
};

export function Perfil() {
  const { usuario, sair } = useAuth();
  const [nome, setNome] = useState(usuario?.nome ?? '');

  const [senhaAtual, setSenhaAtual] = useState('');
  const [novaSenha, setNovaSenha] = useState('');
  const [repetir, setRepetir] = useState('');

  const salvarNome = useMutation({
    mutationFn: () => api<{ ok: true }>('/auth/eu', { metodo: 'PATCH', corpo: { nome } }),
  });

  const trocarSenha = useMutation({
    mutationFn: () =>
      api<{ ok: true }>('/auth/eu/senha', {
        metodo: 'POST',
        corpo: { senhaAtual, novaSenha },
      }),
    onSuccess: () => {
      setSenhaAtual('');
      setNovaSenha('');
      setRepetir('');
    },
  });

  const senhasDiferem = repetir !== '' && novaSenha !== repetir;
  const podeTrocar =
    senhaAtual !== '' && novaSenha.length >= 10 && novaSenha === repetir && novaSenha !== senhaAtual;

  return (
    <div className="max-w-2xl space-y-6">
      <Cartao titulo="Seus dados" descricao={usuario?.email}>
        <div className="space-y-4">
          <Campo rotulo="Nome" valor={nome} onChange={setNome} />

          <div>
            <p className="text-xs font-medium tracking-wide text-slate-600 uppercase">Perfil</p>
            <p className="mt-1 text-sm text-slate-700">
              {PERFIS[usuario?.perfil ?? ''] ?? usuario?.perfil}
            </p>
            <p className="mt-1 text-xs text-slate-500">
              Só um administrador pode alterar seu perfil.
            </p>
          </div>

          {salvarNome.isError && (
            <Aviso tom="erro">
              <p>{salvarNome.error.message}</p>
            </Aviso>
          )}
          {salvarNome.isSuccess && (
            <Aviso tom="info">
              <p>Nome alterado. Saia e entre de novo para o cabeçalho atualizar.</p>
            </Aviso>
          )}

          <Botao
            onClick={() => salvarNome.mutate()}
            carregando={salvarNome.isPending}
            desabilitado={nome.trim().length < 3 || nome === usuario?.nome}
          >
            Salvar nome
          </Botao>
        </div>
      </Cartao>

      <Cartao
        titulo="Trocar senha"
        descricao="Pede a senha atual de propósito: se alguém pegar sua sessão aberta, não consegue te trancar fora da conta."
      >
        <div className="space-y-4">
          <Campo rotulo="Senha atual" tipo="password" valor={senhaAtual} onChange={setSenhaAtual} />
          <Campo
            rotulo="Nova senha"
            tipo="password"
            valor={novaSenha}
            onChange={setNovaSenha}
            dica="Pelo menos 10 caracteres."
          />
          <Campo rotulo="Repita a nova senha" tipo="password" valor={repetir} onChange={setRepetir} />

          {senhasDiferem && (
            <Aviso tom="atencao">
              <p>As duas senhas novas não são iguais.</p>
            </Aviso>
          )}

          {trocarSenha.isError && (
            <Aviso tom="erro">
              <p>{trocarSenha.error.message}</p>
            </Aviso>
          )}

          {trocarSenha.isSuccess && (
            <Aviso tom="info" titulo="Senha alterada">
              <p>
                Suas outras sessões continuam válidas até expirarem. Se você trocou a senha por
                suspeita de acesso indevido, saia e entre de novo em todos os dispositivos.
              </p>
            </Aviso>
          )}

          <div className="flex gap-3">
            <Botao
              onClick={() => trocarSenha.mutate()}
              carregando={trocarSenha.isPending}
              desabilitado={!podeTrocar}
            >
              Trocar senha
            </Botao>
            <Botao tipo="secundario" onClick={sair}>
              Sair da conta
            </Botao>
          </div>
        </div>
      </Cartao>
    </div>
  );
}
